import { AppDispatch } from '../store';
import {
  closeTutorialGuide,
  openTutorialGuide,
  setCanShowTutorialBannerFromOnboarding,
  setShowTutorialBanner,
  setTutorialBannerExpired,
  setTutorialGuideBootstrapResolved,
  setTutorialGuideSessionId
} from '../store/slices/tutorialGuideSlice';
import {
  dismissBanner,
  getLatestOnboardingSession
} from '../services/OnboardingService';
import { LoginResponse } from '../types/Auth.types';
import { startTutorialGuideFlow } from './Functions.utils';

const TUTORIAL_BANNER_VISIBLE_MS = 7 * 24 * 60 * 60 * 1000;

const hasTutorialBannerExpired = (startedAt?: string | null) => {
  if (!startedAt) return false;

  const startedAtTime = new Date(startedAt).getTime();
  if (Number.isNaN(startedAtTime)) return false;

  return Date.now() >= startedAtTime + TUTORIAL_BANNER_VISIBLE_MS;
};

export const handleTutorialGuideAfterLogin = async (
  response: LoginResponse,
  hasAiSystems: boolean,
  dispatch: AppDispatch
) => {
  if (response.isFirstLogin && !response.onboardingStatus) {
    try {
      await startTutorialGuideFlow(dispatch, 'WELCOME_SCREEN');
      dispatch(setTutorialGuideBootstrapResolved(true));
    } catch (error) {
      console.error('Error starting onboarding guide:', error);
    }
    return;
  }

  const tutorialBannerExpired = hasTutorialBannerExpired(
    response.firstOnboardingStartedAt
  );

  dispatch(setTutorialBannerExpired(tutorialBannerExpired));

  if (tutorialBannerExpired) {
    dispatch(setCanShowTutorialBannerFromOnboarding(false));

    try {
      await dismissBanner();
    } catch (error) {
      console.error('Error dismissing onboarding banner:', error);
    }
  }

  try {
    const latestResponse = await getLatestOnboardingSession();
    const latestSession = latestResponse.data;

    if (
      latestSession?.status === 'ONBOARDING_INITIATED' ||
      latestSession?.status === 'ONBOARDING_STEP_VIEW'
    ) {
      dispatch(openTutorialGuide(latestSession.currentStep));
    } else {
      dispatch(closeTutorialGuide());
    }

    dispatch(setTutorialGuideSessionId(latestSession?.id ?? null));

    const canShowTutorialBannerFromOnboarding =
      latestResponse.status !== 404 &&
      !tutorialBannerExpired &&
      !latestSession?.isOnboardingBannerDismissed &&
      !latestSession?.hasCompletedOnboardingOnce;

    dispatch(
      setCanShowTutorialBannerFromOnboarding(
        canShowTutorialBannerFromOnboarding
      )
    );

    dispatch(
      setShowTutorialBanner(
        canShowTutorialBannerFromOnboarding && !hasAiSystems
      )
    );
  } catch (error) {
    console.error('Error fetching onboarding session after login:', error);
    dispatch(setCanShowTutorialBannerFromOnboarding(false));
    dispatch(setShowTutorialBanner(false));
  } finally {
    dispatch(setTutorialGuideBootstrapResolved(true));
  }
};
